import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, Phone, Mail, Globe, BadgeCheck, Home, X } from "lucide-react";
import { homesMicrosite, crmCaptureLead } from "@/lib/api";
import { PropertyCard } from "@/components/homes/PropertyCard";

export default function AgentMicrosite() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); setD(null); setErr(false); homesMicrosite(slug).then(setD).catch(() => setErr(true)); }, [slug]);

  if (err) return <Center><h1 className="text-3xl font-black text-[#0F172A]">Agent site not found</h1><Link to="/homes/agents" className="mt-3 text-[#2563EB]">← Browse agents</Link></Center>;
  if (!d) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;

  const a = d.agent;
  const initials = (a.name || "A").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const site = (a.website || "").replace(/^https?:\/\//, "");
  return (
    <div data-testid="agent-microsite">
      <section className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white">
        <div className="mx-auto max-w-[1100px] px-4 py-14">
          <div className="flex flex-wrap items-center gap-6">
            {a.photo_url ? <img src={a.photo_url} alt={a.name} className="h-32 w-32 rounded-2xl object-cover ring-2 ring-white/20" /> : <span className="flex h-32 w-32 items-center justify-center rounded-2xl bg-white/10 text-4xl font-black ring-2 ring-white/20">{initials}</span>}
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#93B4FF]">{a.title}{a.tier === "local_expert" ? " · Local Expert" : ""}</span>
              <h1 className="flex items-center gap-2 text-4xl font-black md:text-5xl">{a.name}<BadgeCheck className="h-7 w-7 text-[#93B4FF]" /></h1>
              {a.brokerage && <div className="mt-1 text-white/70">{a.brokerage}</div>}
              {(a.city_name || a.state_name) && <div className="mt-2 flex items-center gap-1.5 text-sm text-white/70"><MapPin className="h-4 w-4 text-[#93B4FF]" /> {[a.city_name, a.state_name].filter(Boolean).join(", ")}</div>}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-[1100px] gap-8 px-4 py-10 lg:grid-cols-[1fr_320px]">
        <div>
          {a.bio && (<><h2 className="text-2xl font-black text-[#0F172A]">About</h2><p className="mt-2 leading-relaxed text-slate-600">{a.bio}</p></>)}
          {(a.specialties || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {a.specialties.map((s) => <span key={s} className="rounded-full bg-[#EEF2FF] px-3 py-1.5 text-sm font-medium text-[#2563EB]">{s}</span>)}
            </div>
          )}
          <h2 className="mt-10 flex items-center gap-2 text-2xl font-black text-[#0F172A]"><Home className="h-5 w-5 text-[#2563EB]" /> Listings ({d.listings.length})</h2>
          {d.listings.length ? (
            <div className="mt-4 grid gap-5 sm:grid-cols-2" data-testid="microsite-listings">
              {d.listings.map((l) => <PropertyCard key={l.id} listing={l} />)}
            </div>
          ) : <p className="mt-3 text-slate-500">No active listings yet.</p>}
        </div>

        <aside>
          <div className="sticky top-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="microsite-contact">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Contact {a.name.split(" ")[0]}</div>
            <div className="space-y-2.5 text-sm">
              {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-2 text-[#0F172A] hover:text-[#2563EB]"><Phone className="h-4 w-4 text-[#2563EB]" /> {a.phone}</a>}
              {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-2 break-all text-[#0F172A] hover:text-[#2563EB]"><Mail className="h-4 w-4 text-[#2563EB]" /> {a.email}</a>}
              {site && <a href={a.website.startsWith("http") ? a.website : `https://${site}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 break-all text-[#0F172A] hover:text-[#2563EB]"><Globe className="h-4 w-4 text-[#2563EB]" /> {site}</a>}
              {(a.languages || []).length > 0 && <div className="pt-1 text-slate-500">Speaks: {a.languages.join(", ")}</div>}
            </div>
            <button onClick={() => setContactOpen(true)} className="mt-4 block w-full rounded-full bg-[#2563EB] px-4 py-2.5 text-center text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="microsite-request-call">Request a call</button>
            <Link to={`/homes/card-studio`} className="mt-2 block rounded-full border border-[#2563EB] px-4 py-2.5 text-center text-sm font-bold text-[#2563EB] transition-colors hover:bg-[#EEF2FF]">Get a digital card</Link>
          </div>
        </aside>
      </div>
      {contactOpen && <ContactModal slug={slug} agentName={a.name} onClose={() => setContactOpen(false)} />}
    </div>
  );
}

function ContactModal({ slug, agentName, onClose }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", type: "buyer" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Please enter your name"); return; }
    setBusy(true);
    try {
      await crmCaptureLead({ agent_slug: slug, source: "microsite", source_detail: `${slug} site`, ...f });
      setDone(true);
    } catch { toast.error("Couldn't send. Please try again."); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="microsite-contact-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><h3 className="text-xl font-black text-[#0F172A]">Contact {agentName.split(" ")[0]}</h3><button onClick={onClose} className="text-slate-400 hover:text-[#0F172A]"><X className="h-5 w-5" /></button></div>
        {done ? (
          <div className="py-6 text-center" data-testid="microsite-contact-done">
            <BadgeCheck className="mx-auto h-12 w-12 text-emerald-500" />
            <p className="mt-3 font-bold text-[#0F172A]">Message sent!</p>
            <p className="text-sm text-slate-500">{agentName.split(" ")[0]} will be in touch shortly.</p>
            <button onClick={onClose} className="mt-5 rounded-full bg-[#2563EB] px-6 py-2.5 text-sm font-bold text-white">Done</button>
          </div>
        ) : (
          <div className="space-y-3">
            <input value={f.name} onChange={set("name")} placeholder="Your name *" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#2563EB]" data-testid="contact-name" />
            <div className="grid grid-cols-2 gap-3">
              <input value={f.email} onChange={set("email")} placeholder="Email" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#2563EB]" data-testid="contact-email" />
              <input value={f.phone} onChange={set("phone")} placeholder="Phone" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#2563EB]" data-testid="contact-phone" />
            </div>
            <select value={f.type} onChange={set("type")} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#2563EB]" data-testid="contact-type">
              <option value="buyer">I'm buying</option><option value="seller">I'm selling</option><option value="renter">I'm renting</option><option value="other">Other</option>
            </select>
            <textarea value={f.message} onChange={set("message")} rows={3} placeholder="How can I help?" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#2563EB]" data-testid="contact-message" />
            <button onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#1D4ED8] disabled:opacity-60" data-testid="contact-submit">{busy ? "Sending…" : "Send message"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;
